import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { translations, defaultLang } from '../i18n';

const LanguageContext = createContext(null);
const LANG_STORAGE_KEY = 'mealmatcher_lang';

const readInitialLang = () => {
  if (typeof window === 'undefined') return defaultLang;
  const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
  if (stored === 'no' || stored === 'en') return stored;
  return defaultLang;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(readInitialLang);

  const setLang = useCallback((next) => {
    setLangState(next);
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      /* storage may be blocked — ignore */
    }
  }, []);

  const toggleLang = useCallback(() => {
    setLangState((prev) => {
      const next = prev === 'no' ? 'en' : 'no';
      try {
        window.localStorage.setItem(LANG_STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang === 'no' ? 'nb' : 'en';
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      toggleLang,
      t: translations[lang],
    }),
    [lang, setLang, toggleLang],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
  return ctx;
};
